// MarketRentPage — 市場家賃入力ページ (#market_rent, 2026-06-30)
//
// 店別の市場家賃(想定家賃)を入力 → month_end_setting('market_rent') を UPSERT。
// 月末仕訳「市場家賃振替(定額)」生成 (monthEndGenMarketRent → monthEndBuildFromSetting)
// がこの設定を読むので、ここで更新すれば月末仕訳に反映される。
//   - 借)市場家賃 864550592 / 貸)市場家賃振替 864550593、各店同額 (P/L 中立 net0)、tax=2。
//   - effective_year_month <= 対象月 の最新行が採用される (期初/改定の切替対応)。
//   - 家賃改定・店舗開閉時のみ更新 (= 古い金額に勝手に戻らないための単一入力点)。
//
// 雛形: MonthEndJournalPage の SettingsPanel(section別表+saveMonthEndSetting) / SeibuCashLedgerPage(chrome)。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  PageTitle,
  SectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  Button,
  Text,
  Note,
  MessageBlock,
  InlineSpinner,
  TextField,
} from '@freee_jp/vibes';
import {
  fetchMonthEndSettings,
  saveMonthEndSetting,
  generateMonthEndJournals,
  fetchSectionsMap,
} from '../api/client';

const ACCT_DEBIT = 864550592; // 市場家賃
const ACCT_CREDIT = 864550593; // 市場家賃振替

type Row = { section_id: number | ''; section_name: string; amount: number };

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e6e3df',
  fontSize: '0.75rem', color: '#6b6560', background: '#faf9f7',
};
const td: React.CSSProperties = {
  padding: '6px 10px', borderBottom: '1px solid #f0eeea', fontSize: '0.8125rem',
};

export const MarketRentPage: React.FC = () => {
  const [effYm, setEffYm] = useState<string>(currentYearMonth());
  const [rows, setRows] = useState<Row[]>([]);
  const [note, setNote] = useState<string>('');
  const [loadedEff, setLoadedEff] = useState<string | null>(null);
  const [sectionsMap, setSectionsMap] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    fetchSectionsMap().then(setSectionsMap).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(effYm)) {
      setMsg({ kind: 'error', text: '対象月は YYYY-MM 形式で入力してください' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const res = await fetchMonthEndSettings('market_rent', effYm);
      const lines: any[] = (res.item && res.item.value && res.item.value.lines) || [];
      const debits = lines.filter((l) => l.entry_side === 'debit');
      setRows(
        debits.map((l) => ({
          section_id: l.section_id,
          section_name: l.section_name || '',
          amount: Number(l.amount) || 0,
        })),
      );
      setLoadedEff((res.item && res.item.effective_year_month) || null);
    } catch (e: unknown) {
      setMsg({ kind: 'error', text: `取得失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setLoading(false);
    }
  }, [effYm]);

  // 初回ロード (対象月の現行値)
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [rows]);

  const nameOf = useCallback(
    (sid: number | '', fallback: string): string => {
      if (sid === '') return fallback || '';
      return sectionsMap.get(Number(sid)) || fallback || '';
    },
    [sectionsMap],
  );

  const setAmount = (i: number, v: number) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, amount: v } : r)));
  const setSection = (i: number, v: number | '') =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, section_id: v } : r)));
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const addRow = () =>
    setRows((rs) => [...rs, { section_id: '', section_name: '', amount: 0 }]);

  const onSave = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(effYm)) {
      setMsg({ kind: 'error', text: '適用開始月は YYYY-MM 形式で入力してください' });
      return;
    }
    const valid = rows.filter((r) => r.section_id !== '' && Number(r.amount) > 0);
    if (valid.length === 0) {
      setMsg({ kind: 'error', text: '有効な行 (部門ID + 金額>0) がありません' });
      return;
    }
    // 借)市場家賃 / 貸)市場家賃振替 を各店同額で組成 (P/L 中立)
    const lines: any[] = [];
    for (const r of valid) {
      const sid = Number(r.section_id);
      const sname = nameOf(sid, r.section_name) || null;
      const base = { amount: Number(r.amount), tax_code: 2, section_id: sid, section_name: sname, description: '市場家賃振替(定額)' };
      lines.push({ entry_side: 'debit', account_item_id: ACCT_DEBIT, account_name: '市場家賃', ...base });
      lines.push({ entry_side: 'credit', account_item_id: ACCT_CREDIT, account_name: '市場家賃振替', ...base });
    }
    setSaving(true);
    setMsg(null);
    try {
      await saveMonthEndSetting({
        setting_key: 'market_rent',
        effective_year_month: effYm,
        value_json: { lines },
        note: note || `市場家賃入力ページから更新 (${valid.length}店 計¥${total.toLocaleString()})`,
      });
      setMsg({
        kind: 'success',
        text: `保存しました (適用開始 ${effYm} / ${valid.length}店 計¥${total.toLocaleString()})。「月末仕訳を再生成」で当該月以降の市場家賃振替に反映できます。`,
      });
      await load();
    } catch (e: unknown) {
      setMsg({ kind: 'error', text: `保存失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setSaving(false);
    }
  }, [rows, effYm, note, total, load, nameOf]);

  const onRegenerate = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(effYm)) return;
    setSaving(true);
    setMsg(null);
    try {
      await generateMonthEndJournals(effYm, ['market_rent']);
      setMsg({
        kind: 'info',
        text: `${effYm} の市場家賃振替を再生成しました (登録済は上書きしません)。月末仕訳ページで内容確認 → 承認クリックで登録してください。`,
      });
    } catch (e: unknown) {
      setMsg({ kind: 'error', text: `再生成失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setSaving(false);
    }
  }, [effYm]);

  return (
    <Container>
      <PageTitle>市場家賃入力</PageTitle>
      <Note>
        店別の市場家賃(想定家賃)を入力します。保存すると month_end_setting('market_rent') に反映され、
        月末仕訳「市場家賃振替(定額)」の生成に使われます (借)市場家賃 / 貸)市場家賃振替・各店同額・P/L 中立)。
        家賃改定・店舗開閉時のみ更新してください (= 月末仕訳が古い金額に戻らないための単一入力点)。
      </Note>

      {msg && (
        <div style={{ marginTop: 12 }}>
          <MessageBlock error={msg.kind === 'error'} success={msg.kind === 'success'}>
            {msg.text}
          </MessageBlock>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <CardBase>
          <div style={{ padding: '16px 18px' }}>
            <HStack gap={1} alignItems="end">
              <div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560', marginBottom: 2 }}>
                  適用開始月 (effective_year_month)
                </div>
                <TextField small width="small" value={effYm} placeholder="YYYY-MM"
                  onChange={(e: any) => setEffYm(e.target.value)} />
              </div>
              <Button appearance="secondary" small onClick={load} disabled={loading || saving}>
                この月の現行値を読込
              </Button>
              {loadedEff && <Text size={0.75} color="burnt">現行値の effective: {loadedEff}</Text>}
              {loading && <InlineSpinner isLoading />}
            </HStack>

            <div style={{ marginTop: 16 }}>
              <SectionTitle headlineLevel={3}>店別 市場家賃 (月額)</SectionTitle>
              <ColumnBase paddingSize="zero" border="default" rounded>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 110 }}>部門ID</th>
                      <th style={th}>店舗</th>
                      <th style={{ ...th, textAlign: 'right', width: 180 }}>月額 (円)</th>
                      <th style={{ ...th, width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td style={td}>
                          <TextField small width="small" value={String(r.section_id)}
                            onChange={(e: any) => setSection(i, Number(e.target.value.replace(/[^\d]/g, '')) || '')} />
                        </td>
                        <td style={td}>{nameOf(r.section_id, r.section_name) || '—'}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <TextField small width="full" value={String(r.amount)}
                            onChange={(e: any) => setAmount(i, Number(e.target.value.replace(/[^\d]/g, '')) || 0)} />
                        </td>
                        <td style={td}>
                          <Button appearance="secondary" small onClick={() => removeRow(i)}>削除</Button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={td} colSpan={2}>
                        <Button appearance="secondary" small onClick={addRow}>＋ 店舗を追加</Button>
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>計 ¥{total.toLocaleString()}</td>
                      <td style={td} />
                    </tr>
                  </tbody>
                </table>
              </ColumnBase>
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560', marginBottom: 2 }}>note (任意)</div>
              <TextField small width="full" value={note} placeholder="改定の根拠・経緯など"
                onChange={(e: any) => setNote(e.target.value)} />
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button appearance="secondary" onClick={onRegenerate} disabled={saving || loading}>
                月末仕訳を再生成
              </Button>
              <Button appearance="primary" onClick={onSave} disabled={saving || loading}>
                {saving ? <><InlineSpinner isLoading /> 保存中…</> : '保存'}
              </Button>
            </div>
          </div>
        </CardBase>
      </div>
    </Container>
  );
};

export default MarketRentPage;
